import {
	LuIcon,
	Menu,
	type MenuRootProps,
	type MenuVirtualTriggerProps,
	Text,
	useLayoutContext,
} from "@overmap-ai/blocks"
import { type AssetType, type IssueType, type Stored } from "@overmap-ai/common"
import { selectAssetTypesOfOrganization, selectIssueTypesOfOrganization } from "@overmap-ai/core"
import { useNavigate } from "@tanstack/react-router"
import * as turf from "@turf/turf"
import type { Position } from "geojson"
import { memo, useCallback } from "react"

import { useGeometryPicker } from "../../contexts/hooks"
import { SLIDE_OUT_IDS } from "../../layouts"
import { coordinatesToText } from "../../mapbox/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"
import { selectActiveOrganizationId, selectActiveProjectId } from "../../store/selectors"
import { AssetTypeGroup } from "../MenuGroups/AssetTypeGroup"
import { IssueTypeGroup } from "../MenuGroups/IssueTypeGroup"

enum MapContextMenuPages {
	INITIAL = "initial",
	ISSUE_TYPES = "issue_types",
	ASSET_TYPES = "asset_types",
}

interface MapContextMenuProps extends Omit<MenuRootProps, "children">, MenuVirtualTriggerProps {
	coordinates: Position
}

export const MapContextMenu = memo((props: MapContextMenuProps) => {
	const { coordinates, disabled, virtualElement, ...rest } = props

	const activeProjectId = useAppSelector(selectActiveProjectId)!
	const activeOrganizationId = useAppSelector(selectActiveOrganizationId)!
	const issueTypes = useShallowEqaulSelector(selectIssueTypesOfOrganization(activeOrganizationId))
	const assetTypes = useShallowEqaulSelector(selectAssetTypesOfOrganization(activeOrganizationId))
	const navigate = useNavigate()
	const openGeometryPicker = useGeometryPicker()
	const { setOpen } = useLayoutContext()

	const handleSelectAssetType = useCallback(
		(assetType: Stored<AssetType>) => {
			openGeometryPicker((close) => ({
				onSave: (geometry) => {
					close()

					const [offlineAsset] = sdk.assets.add({
						project: activeProjectId,
						asset_type: assetType.uuid,
						geometry: geometry,
						label: "",
						description: null,
						status: assetType.default_status!,
					})

					void navigate({
						from: "/$organizationId/$projectId",
						to: "/$organizationId/$projectId",
						search: {
							asset: offlineAsset.uuid,
						},
					}).then(() => {
						setOpen(SLIDE_OUT_IDS.RIGHT, true)
					})
				},
				onCancel: close,
			}))
		},
		[activeProjectId, navigate, openGeometryPicker, setOpen],
	)

	const handleSelectIssueType = useCallback(
		(issueType: Stored<IssueType>) => {
			const [offlineIssue, _promise] = sdk.issues.add({
				title: "",
				due_date: null,
				description: null,
				assigned_to: null,
				geo_marker: turf.point(coordinates).geometry,
				project: activeProjectId,
				issue_type: issueType.uuid,
				status: issueType.default_status!,
				priority: issueType.default_priority!,
			})

			void navigate({
				from: "/$organizationId/$projectId",
				to: "/$organizationId/$projectId",
				search: {
					issue: offlineIssue.uuid,
				},
			}).then(() => {
				setOpen(SLIDE_OUT_IDS.RIGHT, true)
			})
		},
		[activeProjectId, coordinates, navigate, setOpen],
	)

	return (
		<Menu.Root modal={false} {...rest}>
			<Menu.VirtualTrigger virtualElement={virtualElement} disabled={disabled} />
			<Menu.Content size="sm">
				<Menu.Label>lat,lng: {coordinatesToText(coordinates, 6)}</Menu.Label>
				<Menu.Separator />
				<Menu.Scroll>
					<Menu.Pages defaultPage={MapContextMenuPages.INITIAL}>
						<Menu.PageContent page={MapContextMenuPages.INITIAL}>
							<Menu.PageTrigger page={MapContextMenuPages.ISSUE_TYPES}>
								<LuIcon icon={modelIconMapping.issue} />
								Issue
							</Menu.PageTrigger>
							<Menu.PageTrigger page={MapContextMenuPages.ASSET_TYPES}>
								<LuIcon icon={modelIconMapping.assetType} />
								Asset
							</Menu.PageTrigger>
						</Menu.PageContent>
						<Menu.PageContent page={MapContextMenuPages.ISSUE_TYPES}>
							{issueTypes.length > 0 ? (
								<IssueTypeGroup issueTypes={issueTypes} onSelectIssueType={handleSelectIssueType} />
							) : (
								<Text accentColor="base">No issue types.</Text>
							)}
						</Menu.PageContent>
						<Menu.PageContent page={MapContextMenuPages.ASSET_TYPES}>
							{assetTypes.length > 0 ? (
								<AssetTypeGroup assetTypes={assetTypes} onSelectAssetType={handleSelectAssetType} />
							) : (
								<Text accentColor="base">No asset types.</Text>
							)}
						</Menu.PageContent>
					</Menu.Pages>
				</Menu.Scroll>
			</Menu.Content>
		</Menu.Root>
	)
})
MapContextMenu.displayName = "MapContextMenu"
